/**
 * Revision System Types
 *
 * Type definitions for the IndexedDB-first revision tracking system.
 * Supports both local-only storage and optional cloud sync for account users.
 */

export interface Revision {
  /** Unique revision ID (UUID) */
  id: string;
  /** File ID this revision belongs to */
  fileId: string;
  /** File path for quick lookups */
  filePath: string;
  /** Full content of this revision */
  content: string;
  /** SHA-256 hash of content for deduplication */
  contentHash: string;
  /** ISO timestamp when revision was created */
  createdAt: string;
  /** Sequential revision number for this file */
  revisionNumber: number;
  /** Parent revision ID for tracking history chain */
  parentRevisionId?: string;
  /** Additional metadata */
  metadata?: RevisionMetadata;
  /** Sync status for account users */
  syncStatus?: SyncStatus;
  /** Size in bytes */
  size: number;
  /** Whether this is a snapshot (full content) or delta */
  isSnapshot: boolean;
  /** Delta from parent revision (if not a snapshot) */
  delta?: string;
}

export interface RevisionMetadata {
  /** User-provided or auto-generated description */
  description?: string;
  /** Tags for categorization */
  tags?: string[];
  /** Change type: edit, create, delete, rename */
  changeType?: 'edit' | 'create' | 'delete' | 'rename';
  /** Number of lines added */
  linesAdded?: number;
  /** Number of lines removed */
  linesRemoved?: number;
  /** Editor used to make the change */
  editorType?: 'monaco' | 'nested' | 'external';
  /** User agent for debugging */
  userAgent?: string;
}

export interface SyncStatus {
  /** Whether this revision is synced to cloud */
  isSynced: boolean;
  /** Last sync attempt timestamp */
  lastSyncAttempt?: string;
  /** Error message if sync failed */
  syncError?: string;
  /** Cloud revision ID after successful sync */
  cloudRevisionId?: string;
  /** Number of sync retry attempts */
  retryCount?: number;
}

export interface DiffResult {
  /** Source revision */
  fromRevision: Revision;
  /** Target revision */
  toRevision: Revision;
  /** Unified diff format */
  unifiedDiff: string;
  /** Additions in the diff */
  additions: number;
  /** Deletions in the diff */
  deletions: number;
  /** Changed lines */
  changes: Array<{
    lineNumber: number;
    type: 'add' | 'delete' | 'modify';
    content: string;
  }>;
}

export interface RevisionHistoryFilter {
  /** Filter by file ID */
  fileId?: string;
  /** Filter by file path */
  filePath?: string;
  /** Filter by date range */
  dateRange?: {
    from: string;
    to: string;
  };
  /** Filter by sync status */
  syncStatus?: 'synced' | 'pending' | 'failed';
  /** Filter by change type */
  changeType?: RevisionMetadata['changeType'];
  /** Limit number of results */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
}

export interface RevisionExport {
  /** Export format version */
  version: string;
  /** Export timestamp */
  exportedAt: string;
  /** Total number of revisions */
  totalRevisions: number;
  /** Revisions grouped by file */
  fileRevisions: Record<string, Revision[]>;
  /** Export metadata */
  metadata: {
    includesDeltas: boolean;
    compressed: boolean;
    exportRange?: {
      from: string;
      to: string;
    };
  };
}

export interface RevisionSettings {
  /** Enable automatic revision creation on save */
  autoRevision: boolean;
  /** Maximum revisions to keep per file (0 = unlimited) */
  maxRevisionsPerFile: number;
  /** Create snapshot every N revisions */
  snapshotInterval: number;
  /** Enable delta compression */
  enableDeltaCompression: boolean;
  /** Enable cloud sync for account users */
  enableCloudSync: boolean;
  /** Auto-prune revisions older than N days (0 = disabled) */
  autoPruneDays: number;
  /** Minimum time between revisions in ms */
  revisionDebounceMs: number;
}

export interface RevisionStats {
  /** Total number of revisions */
  totalRevisions: number;
  /** Total storage used in bytes */
  totalStorageBytes: number;
  /** Number of files with revisions */
  filesWithRevisions: number;
  /** Average revisions per file */
  averageRevisionsPerFile: number;
  /** Oldest revision date */
  oldestRevision?: string;
  /** Newest revision date */
  newestRevision?: string;
  /** Sync statistics for account users */
  syncStats?: {
    syncedCount: number;
    pendingCount: number;
    failedCount: number;
    lastSyncTime?: string;
  };
}

export interface RevisionComparison {
  /** File being compared */
  fileId: string;
  /** Left (older) revision */
  leftRevision: Revision;
  /** Right (newer) revision */
  rightRevision: Revision;
  /** Diff between revisions */
  diff: DiffResult;
  /** Whether changes can be automatically merged */
  canAutoMerge: boolean;
  /** Conflict markers if merge has conflicts */
  conflicts?: Array<{
    startLine: number;
    endLine: number;
    leftContent: string;
    rightContent: string;
  }>;
}

/** IndexedDB database schema version */
export const REVISION_DB_VERSION = 1;

/** IndexedDB database name */
export const REVISION_DB_NAME = 'phrasewright-revisions';

/** Object store names */
export const REVISION_STORES = {
  REVISIONS: 'revisions',
  METADATA: 'revision_metadata',
  SETTINGS: 'revision_settings',
} as const;
